/**
 * SongTitleRename — **改歌名彈窗**（日記頁歌名旁嘅 ✎）
 *
 * 血緣：`decisions/039`（2026-09-25；產品負責人：「i want the song name can be edit」）
 *   · 沿用**對話改名**同一套（`ChatPage` 歷史改名）：`bottom-sheet` ＋ 輸入框 ＋〔儲存〕
 *   · 上限 **40 字**（同 `PATCH /api/chat/sessions/:id`）—— `maxLength` 只係**防呆**，後端都會截
 *
 * ⚠️ 呢個元件**唔發請求**：由父層傳 `onSave`（`hooks/useSongActions.renameTitle`）→ 父層話事
 * ⚠️ 失敗要**照實講**（`onSave` 回 `{ ok:false, error }` → 喺彈窗內顯示錯誤，唔准靜靜收埋）
 */
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import BottomSheet from '@coeldery/bottom-sheet';
import type { LibrarySong } from '../hooks/useLibrary';

export interface SongTitleRenameProps {
  /** 要改名嘅歌（`null` ＝ 彈窗閂住） */
  song: LibrarySong | null;
  busy: boolean;
  /** 父層嘅 `useSongActions().renameTitle`（成功／失敗由父層處理；呢度只管閂窗／顯示錯誤） */
  onSave: (id: string, title: string) => Promise<void>;
  onClose: () => void;
}

export default function SongTitleRename({ song, busy, onSave, onClose }: SongTitleRenameProps) {
  const { t } = useTranslation();
  const [value, setValue] = useState('');
  const [error, setError] = useState<string | null>(null);

  /** 開彈窗 → 帶入現有名（每次換歌重新載入） */
  useEffect(() => {
    setValue(song?.title ?? '');
    setError(null);
  }, [song]);

  const commit = async () => {
    if (!song) return;
    const next = value.replace(/\s+/g, ' ').trim();
    if (!next) {
      setError(t('song_rename_empty'));
      return;
    }
    setError(null);
    /**
     * ⚠️ 成功／失敗都係父層處理（`useSongActions` 會照實報錯 → 父層 `actionError`）。
     *   呢度只負責閂窗（成功）／唔閂（空白名）。
     */
    await onSave(song.id, next);
    onClose();
  };

  return (
    <BottomSheet
      open={song !== null}
      titleKey="song_rename_title"
      onClose={onClose}
      actions={
        <button
          type="button"
          data-testid="song-rename-save"
          disabled={busy}
          onClick={() => void commit()}
          style={{
            width: '100%',
            minHeight: '44px',
            borderRadius: '14px',
            border: 'none',
            background: 'var(--color-primary)',
            color: 'var(--color-on-primary)',
            fontFamily: 'inherit',
            fontSize: 'var(--font-size-small, 15px)',
            fontWeight: 700,
            cursor: busy ? 'default' : 'pointer',
            opacity: busy ? 0.6 : 1,
          }}
        >
          {t('history_save')}
        </button>
      }
    >
      <input
        data-testid="song-rename-input"
        value={value}
        maxLength={40}
        placeholder={t('song_rename_placeholder')}
        onChange={(e) => setValue(e.target.value)}
        style={{
          width: '100%',
          minHeight: '44px',
          padding: '8px 12px',
          borderRadius: '12px',
          border: '1.5px solid var(--color-divider)',
          background: 'var(--color-card)',
          color: 'var(--color-text)',
          fontFamily: 'inherit',
          fontSize: 'var(--font-size-small, 15px)',
        }}
      />
      {error ? (
        <p data-testid="song-rename-error" className="page__error" role="alert" style={{ marginTop: '8px' }}>
          {error}
        </p>
      ) : null}
    </BottomSheet>
  );
}
